import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { verTentativas } from '../../api/repeticao';
import type { FichaDaRepeticao, TentativaAnterior } from '../../api/repeticao';
import { BlocoDaFicha } from '../../Ficha';
import { dataHora, plural } from '../../formato';
import { ErroDaApi, Selo } from '../../ui';

/**
 * As tentativas anteriores, em leitura (RN-260): a encerrada por uma
 * adoção e a desistida. Nada delas vai ao laudo; aqui se vê o que foi
 * medido, quem encerrou e por quê. `render` desenha o retrato de cada uma
 * (a mesma forma do GET da ficha).
 *
 * `versao` muda quando a ficha muda (adotar, desistir): a lista relê.
 */
export function TentativasAnteriores<F = any>({ ensaioId, ficha, render, versao = 0 }: {
  ensaioId: string;
  ficha: FichaDaRepeticao;
  render(retrato: F): ReactNode;
  versao?: number;
}) {
  const [lista, setLista] = useState<TentativaAnterior<F>[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  useEffect(() => {
    let vivo = true;
    verTentativas(ensaioId, ficha)
      .then((l) => { if (vivo) setLista(l as TentativaAnterior<F>[]); })
      .catch((e) => { if (vivo) setErro(e); });
    return () => { vivo = false; };
  }, [ensaioId, ficha, versao]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!lista?.length) return null;
  return (
    <BlocoDaFicha titulo="Tentativas anteriores" classe="repeticao-anteriores"
      selo={<Selo tom="neutro">{plural(lista.length, 'tentativa', 'tentativas')}</Selo>}
      sub="Em leitura. Ficam fora da conta, das pendências e do laudo.">
      <div className="fe-miolo">
        {lista.map((t) => (
          <details key={t.numero} className="repeticao-tentativa">
            <summary>
              <b>Tentativa {t.numero}</b>
              {' · '}
              {t.situacao === 'desistida' ? 'desistida' : 'encerrada pela adoção de outra'}
              {t.encerradaEm && <> · {dataHora(t.encerradaEm)}</>}
              {t.encerradaPor && <> · {t.encerradaPor}</>}
              {t.recalculado && <> · recalculado com as Condições de hoje</>}
            </summary>
            {t.motivo && <p className="sub">Motivo: {t.motivo}</p>}
            {t.recalculado && (
              <p className="sub repeticao-recalculado">
                A ficha desta tentativa não foi guardada quando ela saiu: o que se vê
                aqui foi relido agora e pode não ser o que foi emitido.
              </p>
            )}
            {render(t.retrato)}
          </details>
        ))}
      </div>
    </BlocoDaFicha>
  );
}
